import { showUserAlert } from "../utils/userErrorMessage";
import React, { useEffect, useState } from "react";
import Header from "./Header";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { supabase } from "../supabase/client";
import { useLocation, useNavigate } from "react-router-dom";

  const getToday = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getSearchFilters = (search) => {
    const params = new URLSearchParams(search);
    return {
      fromDate: params.get("from") || getToday(),
      toDate: params.get("to") || params.get("from") || getToday(),
      type: params.get("type") || "",
      busType: params.get("busType") || "",
      user: params.get("user") || "",
      start: params.get("start") || "",
      destination: params.get("dest") || "",
    };
  };

  const fetchAllRows = async (query) => {
    const pageSize = 1000;
    let from = 0;
    const rows = [];

    while (true) {
      const { data, error } = await query.range(from, from + pageSize - 1);
      if (error) throw error;

      const page = data || [];
      rows.push(...page);

      if (page.length < pageSize) break;
      from += pageSize;
    }

    return rows;
  };

export default function ReservationSearch() {
  const location = useLocation();
  const initialFilters = getSearchFilters(location.search);
  const [fromDate, setFromDate] = useState(initialFilters.fromDate);
  const [toDate, setToDate] = useState(initialFilters.toDate);
  const [type, setType] = useState(initialFilters.type);
  const [busType, setBusType] = useState(initialFilters.busType);
  const [user, setUser] = useState(initialFilters.user);
  const [start, setStart] = useState(initialFilters.start);
  const [destination, setDestination] = useState(initialFilters.destination);
  const [results, setResults] = useState([]);
  const navigate = useNavigate();


  const handleSearch = async (overrides = {}) => {
    const safeOverrides = overrides?.preventDefault ? {} : overrides;
    const filters = {
      fromDate,
      toDate,
      type,
      busType,
      user,
      start,
      destination,
      ...safeOverrides,
    };
    const company = localStorage.getItem("email");

    let query = supabase
      .from("reservation_summary")
      .select("*")
      .eq("company", company);


    if (filters.type) query = query.eq("reservation_type", filters.type);
    if (filters.busType) query = query.eq("car_type", filters.busType);
    if (filters.user) query = query.ilike("user", `%${filters.user}%`);
    if (filters.start) query = query.ilike("start", `%${filters.start}%`);
    if (filters.destination) query = query.ilike("destination", `%${filters.destination}%`);

    // ✅ 날짜 겹침 조건. end_date가 비어 있는 예전 row는 start_date 기준으로 포함.
    query = query.or(
      [
        `and(start_date.lte.${filters.toDate},end_date.gte.${filters.fromDate})`,
        `and(start_date.gte.${filters.fromDate},start_date.lte.${filters.toDate},end_date.is.null)`,
      ].join(",")
    );

    try {
      if (process.env.NODE_ENV !== "production") console.log("[ReservationSearch] filters:", filters);
      const data = await fetchAllRows(query);
      if (process.env.NODE_ENV !== "production") console.log("[ReservationSearch] reservation_summary rows:", data);
      if (process.env.NODE_ENV !== "production") console.log("[ReservationSearch] row count:", data.length);
      const priority = {
        "일반": 1,
        "학단": 2,
        "투어": 3,
        "관광": 3,
        "통근": 4,
      };

      const sorted = data
        .slice()
        .sort((a, b) => {
          const dateA = new Date(a.start_date);
          const dateB = new Date(b.start_date);
          if (dateA < dateB) return -1;
          if (dateA > dateB) return 1;

          const pA = priority[a.reservation_type] || 99;
          const pB = priority[b.reservation_type] || 99;
          return pA - pB;
        });

      setResults(sorted);
    } catch (error) {
      showUserAlert("검색 중 오류 발생: " + error.message);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!Array.from(params.keys()).length) return;

    const filters = getSearchFilters(location.search);
    setFromDate(filters.fromDate);
    setToDate(filters.toDate);
    setType(filters.type);
    setBusType(filters.busType);
    setUser(filters.user);
    setStart(filters.start);
    setDestination(filters.destination);
    handleSearch(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <div className="space-y-1">
      <Header />
      <div className="px-4 pt-1 pb-4">
      <h2 className="text-lg font-semibold">예약 검색</h2>

      <Card className="border-0 shadow-none">
        <CardContent className="space-y-4 bg-white p-4">
          <div className="flex gap-4 items-center">
            <label className="font-semibold">운행일</label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <span>~</span>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>

          <div className="flex gap-4 items-center">
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">구분</option>
              <option>일반</option>
              <option>학단</option>
              <option>투어</option>
              <option>통근</option>
              {/*<option>기업</option>
              <option>여행사</option>
              <option>통학</option>
              <option>기사님</option>*/}
            </select>
            <select value={busType} onChange={(e) => setBusType(e.target.value)}>
              <option value="">차종</option>
              <option>대형</option>
              <option>중형</option>
              <option>우등</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <Input placeholder="사용자/노선명" value={user} onChange={(e) => setUser(e.target.value)} />
            <Input placeholder="출발지" value={start} onChange={(e) => setStart(e.target.value)} />
            <Input placeholder="행선지" value={destination} onChange={(e) => setDestination(e.target.value)} />
            <Button onClick={() => handleSearch()}>검색</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="max-h-[70vh] overflow-auto bg-white">
          <table className="table-fixed w-full border border-gray-300 text-sm">
            <thead className="bg-blue-500 text-white">
              <tr>
                <th className="sticky top-0 z-20 border bg-blue-500 p-1 w-[40px] text-center">순번</th>
                <th className="sticky top-0 z-20 border bg-blue-500 p-1 w-[210px] text-center">운행기간(박 수)</th>
                <th className="sticky top-0 z-20 border bg-blue-500 p-1 w-[42px] text-center">대수</th>
                <th className="sticky top-0 z-20 border bg-blue-500 p-1 w-[52px] text-center">구분</th>
                <th className="sticky top-0 z-20 border bg-blue-500 p-2 w-[190px]">사용자/노선명</th>
                <th className="sticky top-0 z-20 border bg-blue-500 p-1 w-[68px] text-center">출발시간</th>
                <th className="sticky top-0 z-20 border bg-blue-500 p-2 w-[175px] text-center">출발지</th>
                <th className="sticky top-0 z-20 border bg-blue-500 p-2 w-[175px] text-center">경유지</th>
                <th className="sticky top-0 z-20 border bg-blue-500 p-2 w-[175px] text-center">행선지</th>
                <th className="sticky top-0 z-20 border bg-blue-500 p-1 w-[80px] text-center">받을 금액</th>
                <th className="sticky top-0 z-20 border bg-blue-500 p-1 w-[80px] text-center">기사 금액</th>
              </tr>
            </thead>
            <tbody> 
            {results.map((row, index) => (
              <tr key={row.id}>
                <td className="border p-1 w-[40px] text-center">
                  <button
                    className="text-blue-600 underline"
                    onClick={() => navigate(`/reservation-edit/${row.group_id}`)}
                  >
                    {index + 1}
                  </button>
                </td>
                <td className="border p-1 w-[210px] text-center whitespace-nowrap overflow-hidden">
                  {row.start_date} ~ {row.end_date} ({row.days}박)
                </td>
                <td className="border p-1 w-[42px] text-center">{row.number_of_car}</td>
                <td className="border p-1 w-[52px] text-center">{row.reservation_type}</td>
                <td className="border p-2 w-[190px]">{row.user}</td>
                <td className="border p-1 w-[68px] text-center whitespace-nowrap">{row.time?.slice(0, 5)}</td>
                <td className="border p-2 w-[175px]">{row.start}</td>
                <td className="border p-2 w-[175px]">{row.transit}</td>
                <td className="border p-2 w-[175px]">{row.destination}</td>
                <td className="border p-1 w-[80px] text-right whitespace-nowrap">{row.money?.toLocaleString() ?? ""}</td>
                <td className="border p-1 w-[80px] text-right whitespace-nowrap">{row.driver_money?.toLocaleString() ?? ""}</td>
              </tr>
            ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
    </div>
  );
}
